/**
 * Deslizar el dedo de lado para pasar a la siguiente o a la anterior: lo usan
 * el carrusel de la portada (`useRotacion`) y la galeria de la ficha.
 *
 * Solo mira donde se apoyo el dedo y donde se levanto, sin `preventDefault`:
 * la pagina se sigue bajando con el dedo como siempre, y un deslizamiento que
 * fue mas de alto que de lado no cuenta, porque era bajar la pagina.
 */
import { useCallback, useRef } from 'react';
import type { TouchEvent } from 'react';

/** Cuanto hay que deslizar el dedo, en pixeles, para que cuente. */
const DESLIZAMIENTO_MINIMO = 40;

export interface ManejadoresDeslizamiento {
  onTouchStart(evento: TouchEvent<HTMLElement>): void;
  onTouchEnd(evento: TouchEvent<HTMLElement>): void;
}

/** `alDeslizar` recibe 1 si el dedo fue hacia la izquierda (siguiente) y -1 si fue hacia la derecha. */
export function useDeslizamiento(alDeslizar: (salto: 1 | -1) => void): ManejadoresDeslizamiento {
  /** Donde se apoyo el dedo, para medir el deslizamiento al levantarlo. */
  const inicioToque = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((evento: TouchEvent<HTMLElement>): void => {
    const toque = evento.touches[0];
    // Con dos dedos es ampliar la foto, no deslizar.
    inicioToque.current =
      toque !== undefined && evento.touches.length === 1 ? { x: toque.clientX, y: toque.clientY } : null;
  }, []);

  const onTouchEnd = useCallback(
    (evento: TouchEvent<HTMLElement>): void => {
      const inicio = inicioToque.current;
      const toque = evento.changedTouches[0];
      inicioToque.current = null;
      if (inicio === null || toque === undefined) return;

      const dx = toque.clientX - inicio.x;
      const dy = toque.clientY - inicio.y;
      if (Math.abs(dx) < DESLIZAMIENTO_MINIMO || Math.abs(dx) <= Math.abs(dy)) return;
      alDeslizar(dx < 0 ? 1 : -1);
    },
    [alDeslizar],
  );

  return { onTouchStart, onTouchEnd };
}
